import {
	AlertCircle,
	Building2,
	Check,
	CheckCircle2,
	ClipboardList,
	PlayCircle,
	User,
	UserCheck,
	XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui';
import { PublicSessionAccessLinkField } from '@/components/ui/PublicSessionAccessLinkField';
import { SessionWhatsAppNotificationsPanel } from '@/components/whatsapp/SessionWhatsAppNotificationsPanel';
import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import { ProfessionalProntuarioModal } from './ProfessionalProntuarioModal';
import { cn } from '@/lib/utils';
import { SessionCancellationNotice } from '@/components/session/SessionCancellationNotice';
import {
	getSessionCallCompanyBanner,
	isSessionTranscriptionAllowed,
} from '@/constants/sessionCall';
import {
	useGetPatient,
	useGetSession,
	useGetSessionVideoRoom,
	usePatchSessionEnd,
} from '@/services';
import { useProfessionalSessionAccessWindow } from '@/hooks/useProfessionalSessionAccessWindow';
import { openProfessionalSessionCall } from '@/utils/professionalSessionCall';
import { isP2PTranscriptionOnlySessionType } from '@/utils/sessionType';
import { buildPublicSessionAccessUrl, resolveVideoRoomId } from '@/utils';
import {
	getProfessionalSessionBookingSource,
	canProfessionalShareP2PPatientAccessLink,
	PROFESSIONAL_SESSION_BOOKING_SOURCE_COPY,
	type ProfessionalSessionBookingSource,
} from '@/utils/sessionScheduledBy';
import { peekProntuarioSessionNotes } from '@/pages/professional/sessao-chamada/utils/prontuarioSessionNotesStorage';

interface SessionDetailModalProps {
	session: Sessions.Session;
}

export const STATUS_THEME_COLORS = {
	CANCELLED: 'bg-red-600 text-red-600 hover:bg-red-700',
	FINISHED: 'bg-zinc-600 text-zinc-600 hover:bg-zinc-700',
	STARTED: 'bg-orange-600 text-orange-600 hover:bg-orange-700',
	CREATED: 'bg-amber-600 text-amber-600 hover:bg-amber-700',
	CONFIRMED: 'bg-emerald-600 text-emerald-600 hover:bg-emerald-700',
} as Record<Sessions.SessionStatus, string>;

const STATUS_INFO = {
	CANCELLED: {
		label: 'CANCELADO',
		badge: 'bg-red-50 text-red-700 border-red-100',
		icon: <XCircle size={20} />,
	},
	FINISHED: {
		label: 'FINALIZADO',
		badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
		icon: <Check size={20} />,
	},
	STARTED: {
		label: 'EM ANDAMENTO',
		badge: 'bg-orange-50 text-orange-700 border-orange-200',
		icon: <PlayCircle size={20} />,
	},
	CREATED: {
		label: 'AGUARDANDO PAGAMENTO',
		badge: 'bg-amber-50 text-amber-700 border-amber-200',
		icon: <AlertCircle size={20} />,
	},
	CONFIRMED: {
		label: 'CONFIRMADO',
		badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
		icon: <CheckCircle2 size={20} />,
	},
};

const BOOKING_SOURCE_ICON: Record<
	ProfessionalSessionBookingSource,
	typeof UserCheck
> = {
	self: UserCheck,
	plumaa_user: User,
	plumaa_team: Building2,
};

export const SessionDetailModal = ({ session }: SessionDetailModalProps) => {
	const navigate = useNavigate();
	const { data: fetchedSession } = useGetSession(session.id, {
		staleTime: 0,
		refetchOnMount: 'always',
	});
	const activeSession = fetchedSession ?? session;
	const companyBanner = getSessionCallCompanyBanner(activeSession);
	const [currentStatus, setCurrentStatus] = useState<Sessions.SessionStatus>(
		session.status,
	);
	const [isJoiningCall, setIsJoiningCall] = useState(false);
	const [isEndSessionConfirmOpen, setIsEndSessionConfirmOpen] =
		useState(false);
	const [isProntuarioOpen, setIsProntuarioOpen] = useState(false);

	const medicalRecordSession = activeSession.medical_record_session ?? null;
	const hasProntuario = medicalRecordSession != null;

	useEffect(() => {
		setCurrentStatus(session.status);
	}, [session.status, session.id]);

	const isProfessionalAccessUnlocked =
		useProfessionalSessionAccessWindow(activeSession);
	const canEnterOnlineSession =
		activeSession.is_online &&
		(currentStatus === 'CONFIRMED' || currentStatus === 'STARTED');

	const canEnterTranscriptionOnlySession =
		isP2PTranscriptionOnlySessionType(activeSession.type) &&
		(currentStatus === 'CONFIRMED' || currentStatus === 'STARTED');

	const { mutate: endSession, isPending: isEnding } = usePatchSessionEnd(
		() => {
			setCurrentStatus('FINISHED');
		},
	);

	const requestEndSessionConfirmation = () => {
		setIsEndSessionConfirmOpen(true);
	};

	const isGroupSession = activeSession.type === 'GROUP';
	const startDate = new Date(activeSession.selected_date);
	const endDate = new Date(startDate.getTime() + 50 * 60000);
	const statusInfo = STATUS_INFO[currentStatus] || STATUS_INFO.CREATED;
	const apiParticipants = activeSession.participants;

	const participantsList =
		apiParticipants?.map((participant, index) => ({
			id:
				participant.user_id ??
				participant.patient_id ??
				participant.id ??
				String(index),
			first_name:
				participant.user?.first_name ??
				participant.patient?.full_name?.split(' ')[0] ??
				'',
			last_name:
				participant.user?.last_name ??
				participant.patient?.full_name?.split(' ').slice(1).join(' ') ??
				'',
		})) ?? [];

	const isSingleParticipantGroup =
		isGroupSession && participantsList.length === 1;

	const theme =
		STATUS_THEME_COLORS[currentStatus] || 'bg-zinc-600 text-zinc-600';

	const primaryFromParticipants = participantsList[0] ?? {
		first_name: 'Paciente',
		last_name: '',
	};

	const patientFirstName =
		activeSession.user?.first_name ??
		activeSession.first_name ??
		primaryFromParticipants.first_name;
	const patientLastName =
		activeSession.user?.last_name ??
		activeSession.last_name ??
		primaryFromParticipants.last_name;

	const formattedDate = startDate.toLocaleDateString('pt-BR', {
		weekday: 'long',
		day: 'numeric',
		month: 'long',
	});
	const timeRange = `${startDate.toLocaleTimeString('pt-BR', {
		hour: '2-digit',
		minute: '2-digit',
	})} - ${endDate.toLocaleTimeString('pt-BR', {
		hour: '2-digit',
		minute: '2-digit',
	})}`;

	const canSendWhatsAppReminder =
		canProfessionalShareP2PPatientAccessLink(activeSession);

	const canShareBySessionStatus =
		currentStatus === 'CONFIRMED' || currentStatus === 'STARTED';

	const roomIdFromSession = activeSession.video_room
		? resolveVideoRoomId(activeSession.video_room)
		: null;

	const shouldFetchVideoRoomForShare =
		canSendWhatsAppReminder &&
		activeSession.is_online &&
		canShareBySessionStatus &&
		!roomIdFromSession;

	const { data: videoRoomCredentials } = useGetSessionVideoRoom(
		activeSession.id,
		{
			enabled: shouldFetchVideoRoomForShare,
			retry: 1,
		},
	);

	const resolvedRoomId =
		roomIdFromSession ??
		(videoRoomCredentials?.url
			? resolveVideoRoomId({ room_url: videoRoomCredentials.url })
			: null);

	const patientAccessLinkUserName = patientFirstName?.trim() || undefined;
	const patientAccessUrl =
		activeSession.is_online && resolvedRoomId && canShareBySessionStatus
			? buildPublicSessionAccessUrl(
					resolvedRoomId,
					patientAccessLinkUserName,
			  )
			: null;

	const patientParticipant = useMemo(
		() =>
			apiParticipants?.find(
				(participant) =>
					participant.entity_type === 'PATIENT' ||
					participant.patient_id != null,
			),
		[apiParticipants],
	);

	const reminderPatientId =
		patientParticipant?.patient_id ??
		patientParticipant?.patient?.id ??
		null;

	const bookingSource = getProfessionalSessionBookingSource(activeSession);
	const bookingSourceCopy =
		PROFESSIONAL_SESSION_BOOKING_SOURCE_COPY[bookingSource];
	const BookingSourceIcon = BOOKING_SOURCE_ICON[bookingSource];

	const { data: reminderPatientProfile } = useGetPatient(
		reminderPatientId ?? '',
		{
			enabled: canSendWhatsAppReminder && !!reminderPatientId,
		},
	);

	const reminderPatientName =
		reminderPatientProfile?.full_name?.trim() ||
		patientParticipant?.patient?.full_name?.trim() ||
		`${patientFirstName} ${patientLastName}`.trim() ||
		undefined;

	const reminderPatientPhone =
		reminderPatientProfile?.phone ??
		patientParticipant?.patient?.phone ??
		activeSession.user?.phone_number ??
		null;

	const showPatientReminderAction =
		canSendWhatsAppReminder && patientAccessUrl != null;

	const hasSessionActions =
		canEnterOnlineSession ||
		canEnterTranscriptionOnlySession ||
		showPatientReminderAction ||
		currentStatus === 'STARTED';

	// Dados básicos do participante já presentes na resposta do backend
	const primaryParticipantName =
		patientParticipant?.patient?.full_name?.trim() ||
		activeSession.display_name?.trim() ||
		`${patientFirstName} ${patientLastName}`.trim() ||
		'Paciente';

	const primaryParticipantPhone =
		patientParticipant?.patient?.phone?.trim() ||
		activeSession.user?.phone_number?.trim() ||
		null;

	const multipleParticipants =
		(apiParticipants?.length ?? 0) > 1 ||
		(isGroupSession && !isSingleParticipantGroup);

	const showTranscriptionStatus =
		(activeSession.is_online ||
			isP2PTranscriptionOnlySessionType(activeSession.type)) &&
		(activeSession.type === 'P2P' ||
			activeSession.type === 'P2P_TRANSCRIPTION_ONLY' ||
			activeSession.type === 'DEFAULT') &&
		(typeof activeSession.transcription_allowed === 'boolean' ||
			activeSession.transcription_mock === true);

	const showWhatsAppNotifications =
		(activeSession.type === 'P2P' ||
			activeSession.type === 'P2P_TRANSCRIPTION_ONLY') &&
		activeSession.whatsapp_notifications !== undefined;

	return (
		<>
			<div className="space-y-5 antialiased md:space-y-6">
				{/* Seção: info da sessão */}
				<section className="space-y-4 rounded-2xl border border-zinc-100 bg-zinc-50/80 p-4 md:p-5">
					<div className="flex items-start gap-3 md:gap-4">
						<div
							className={cn(
								'mt-1 h-6 w-6 shrink-0 rounded-md md:h-7 md:w-7',
								theme.split(' ')[0],
							)}
						/>
						<div className="min-w-0 space-y-1">
							<p className="text-sm capitalize text-zinc-500 md:text-base">
								{formattedDate} • {timeRange}
							</p>
							<p className="text-xs text-zinc-400 md:text-sm">
								{isP2PTranscriptionOnlySessionType(
									activeSession.type,
								)
									? 'Consulta com transcrição (sem videochamada Plumaa)'
									: `Consulta ${
											activeSession.is_online
												? 'Online'
												: 'Presencial'
									  }`}
							</p>
						</div>
					</div>

					<div className="flex items-center gap-3">
						<div
							className={cn(
								'flex w-5 shrink-0 justify-center md:w-6',
								theme.split(' ')[1],
							)}
						>
							{statusInfo.icon}
						</div>
						<div
							className={cn(
								'rounded-md border px-3 py-1 text-[10px] font-black tracking-wider',
								statusInfo.badge,
							)}
						>
							{statusInfo.label}
						</div>
					</div>

					<div className="flex items-start gap-1.5 text-xs leading-relaxed text-zinc-400">
						<BookingSourceIcon
							className="mt-px size-3 shrink-0 text-zinc-400"
							strokeWidth={2}
						/>
						<div className="min-w-0">
							<p className="text-zinc-500">
								{bookingSourceCopy.label}
							</p>
							<p>{bookingSourceCopy.description}</p>
						</div>
					</div>

					<SessionCancellationNotice
						session={activeSession}
						viewer="professional"
					/>

					{showTranscriptionStatus ? (
						<div
							className={cn(
								'rounded-xl border px-3 py-2.5 text-xs leading-relaxed',
								isSessionTranscriptionAllowed(activeSession)
									? 'border-emerald-100 bg-emerald-50/80 text-emerald-800'
									: 'border-zinc-200 bg-zinc-50 text-zinc-600',
							)}
						>
							<p className="font-semibold">
								{isSessionTranscriptionAllowed(activeSession)
									? 'Transcrição habilitada'
									: 'Transcrição desabilitada'}
							</p>
							<p className="mt-0.5 text-[11px] opacity-90">
								{isSessionTranscriptionAllowed(activeSession)
									? 'Esta sessão será transcrita e poderá gerar prontuário com IA ao final.'
									: 'Esta sessão não será transcrita. Use o prontuário para anotações manuais.'}
							</p>
						</div>
					) : null}

					{hasSessionActions ? (
						<div className="space-y-2 border-t border-zinc-100 pt-4">
							<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
								{(canEnterOnlineSession ||
									canEnterTranscriptionOnlySession) && (
									<Button
										type="button"
										text={
											isJoiningCall
												? 'Entrando...'
												: isProfessionalAccessUnlocked
												  ? canEnterTranscriptionOnlySession
														? 'Iniciar transcrição'
														: 'Entrar na sessão'
												  : 'Disponível 5 min antes'
										}
										className={cn(
											'h-10 rounded-lg border-none px-4 text-sm font-semibold text-white shadow-sm md:h-11 md:px-6',
											theme.split(' ')[0],
											(!isProfessionalAccessUnlocked ||
												isJoiningCall) &&
												'cursor-not-allowed opacity-45 saturate-0',
										)}
										disabled={
											!isProfessionalAccessUnlocked ||
											isJoiningCall
										}
										onClick={() => {
											if (!isProfessionalAccessUnlocked) {
												toast.info(
													'Só é possível entrar nos últimos 5 minutos antes do horário agendado.',
												);
												return;
											}

											void openProfessionalSessionCall({
												sessionId: session.id,
												navigate,
												sessionStatus: currentStatus,
												sessionType: session.type,
												isOnline:
													activeSession.is_online,
												transcriptionAllowed:
													isSessionTranscriptionAllowed(
														activeSession,
													),
												transcriptionMock:
													activeSession.transcription_mock ===
													true,
												companyBanner,
												onSessionStarted: () =>
													setCurrentStatus('STARTED'),
												onStart: () =>
													setIsJoiningCall(true),
												onFinally: () =>
													setIsJoiningCall(false),
											});
										}}
									/>
								)}
								{showPatientReminderAction ? (
									<PublicSessionAccessLinkField
										url={patientAccessUrl}
										patientName={reminderPatientName}
										phone={reminderPatientPhone}
										variant="session-modal"
									/>
								) : null}
								{currentStatus === 'STARTED' && (
									<Button
										type="button"
										text={
											isEnding
												? 'Encerrando...'
												: 'Encerrar sessão'
										}
										className="h-10 rounded-lg bg-zinc-900 px-4 text-sm font-semibold text-white shadow-sm hover:bg-zinc-800 md:h-11 md:px-6"
										disabled={isEnding}
										onClick={requestEndSessionConfirmation}
									/>
								)}
							</div>
							{(canEnterOnlineSession ||
								canEnterTranscriptionOnlySession) &&
								!isProfessionalAccessUnlocked && (
									<p className="max-w-md text-xs text-zinc-500">
										Só é possível entrar nos últimos 5
										minutos antes do horário agendado.
									</p>
								)}
						</div>
					) : null}
				</section>

				{/* Seção: paciente + notificações WhatsApp */}
				<section className="rounded-2xl border border-zinc-100 bg-white p-4 md:p-5">
					<div className="flex items-center justify-between gap-3">
						{multipleParticipants ? (
							<div className="min-w-0 flex-1 space-y-3">
								{apiParticipants?.map((participant, index) => {
									const name =
										participant.patient?.full_name?.trim() ||
										`${
											participant.user?.first_name ?? ''
										} ${
											participant.user?.last_name ?? ''
										}`.trim() ||
										'Participante';
									const phone =
										participant.patient?.phone?.trim() ||
										participant.user?.phone_number?.trim() ||
										null;
									return (
										<div
											key={participant.id ?? index}
											className="flex items-center gap-3"
										>
											<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold uppercase text-blue-700 ring-2 ring-blue-200">
												{name.charAt(0).toUpperCase()}
											</div>
											<div className="min-w-0">
												<p className="truncate text-sm font-semibold text-zinc-800">
													{name}
												</p>
												{phone && (
													<p className="text-xs text-zinc-400">
														{phone}
													</p>
												)}
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<div className="flex min-w-0 flex-1 items-center gap-3">
								<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold uppercase text-blue-700 ring-2 ring-blue-200">
									{primaryParticipantName
										.charAt(0)
										.toUpperCase()}
								</div>
								<div className="min-w-0">
									<p className="truncate text-sm font-semibold text-zinc-800">
										{primaryParticipantName}
									</p>
									{primaryParticipantPhone && (
										<p className="text-xs text-zinc-400">
											{primaryParticipantPhone}
										</p>
									)}
								</div>
							</div>
						)}

						{hasProntuario ? (
							<button
								type="button"
								onClick={() => setIsProntuarioOpen(true)}
								className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-zinc-900 px-4 text-sm font-semibold text-white hover:bg-zinc-800 md:h-11"
							>
								<ClipboardList
									className="size-4 shrink-0"
									strokeWidth={2.25}
									aria-hidden
								/>
								Ver prontuário do paciente
							</button>
						) : null}
					</div>

					{showWhatsAppNotifications ? (
						<>
							<div
								className="my-4 border-t border-zinc-100"
								aria-hidden
							/>
							<SessionWhatsAppNotificationsPanel
								embedded
								notifications={
									activeSession.whatsapp_notifications ?? []
								}
								patientName={primaryParticipantName}
								patientPhone={primaryParticipantPhone}
							/>
						</>
					) : null}
				</section>
			</div>

			<ConfirmActionModal
				isOpen={isEndSessionConfirmOpen}
				onClose={() => setIsEndSessionConfirmOpen(false)}
				onConfirm={() => {
					setIsEndSessionConfirmOpen(false);

					const rawNotes = peekProntuarioSessionNotes(
						activeSession.id,
						activeSession.selected_date,
					);

					endSession({
						id: activeSession.id,
						raw_notes: (rawNotes ?? '').trim() || undefined,
					});
				}}
				title="Encerrar sessão"
				description={
					<span>
						Tem certeza que deseja encerrar esta sessão? Essa ação
						marca a{' '}
						<span className="font-semibold text-red-500">
							sessão como finalizada
						</span>
						.
					</span>
				}
				confirmButtonText={
					isEnding ? 'Encerrando...' : 'Confirmar encerramento'
				}
				disableConfirm={isEnding}
			/>

			{hasProntuario && isProntuarioOpen && (
				<ProfessionalProntuarioModal
					isOpen={isProntuarioOpen}
					onClose={() => setIsProntuarioOpen(false)}
					session={activeSession}
					initialView="sessions-list"
				/>
			)}
		</>
	);
};
